import { Box } from '@chakra-ui/react';
import { EditorContent } from '@tiptap/react';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { memo, useRef } from 'react';
import { useEditor as useInfographEditor } from 'widgets/sdk';

import { findFirstFontFamily } from '../common/TextBasedWidgets.helpers';
import { WidgetState } from '../common/TextBasedWidgets.types';
import { isPositionWithinCurrentSelection } from './TableWidget.helpers';
import { TableEditorProps } from './TableWidget.types';

export const TableEditor = memo(({ widgetState, editor, onBlur, caretColor }: TableEditorProps) => {
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const { boundingBox, hideContextMenu } = useInfographEditor();

  const isEditable = widgetState === WidgetState.Edit;
  const isActive = widgetState === WidgetState.Active;

  const fitBoundingBoxToTextWhileTyping = ({ height }: { height: number | undefined }) => {
    if ((isEditable || isActive) && typeof height === 'number') {
      boundingBox.updateRect();
    }
  };

  useResizeObserver({ ref: tableWrapperRef.current, onResize: fitBoundingBoxToTextWhileTyping });

  if (!editor) return <></>;

  const stopPropagation = (e: React.KeyboardEvent | React.MouseEvent) => {
    if (isEditable) e.stopPropagation();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    hideContextMenu();
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Allow context menu handler in wrapper component to trigger
    // when right-click on cell
    if (isEditable && e.button === 2) {
      // If right-click event occurs outside of the current selection, should update the selection state
      // Otherwise prevent updating selection state
      if (!isPositionWithinCurrentSelection(editor, { top: e.clientY, left: e.clientX })) return;
      e.preventDefault();
    } else {
      hideContextMenu();
      stopPropagation(e);
    }
  };

  const fontFamily = findFirstFontFamily(editor);

  return (
    <Box
      _hover={{ cursor: isEditable ? 'text' : 'default' }}
      h='fit-content'
      w='100%'
      cursor={isEditable ? '' : 'move !important'}
      position='relative'
      onClick={stopPropagation}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      onBlur={onBlur}
      ref={tableWrapperRef}
      pointerEvents={widgetState !== WidgetState.Default ? 'auto' : 'none'}
      // The input cursor color set to the same color as the text (applies for blank lines without text)
      color={caretColor}
      // for line-height consistency when exporting to pdf - the parent div should have the same font family as the editor
      // obs: there are probably edge-cases here with multiple fonts in the same text widget
      fontFamily={fontFamily}
    >
      <EditorContent editor={editor} />
    </Box>
  );
});
TableEditor.displayName = 'TableEditor';
